import { useState, useEffect, useRef } from 'react'
import { ethers } from 'ethers'
import './MyDashboard.css'
import { API_URL } from './config/api'

interface MyDashboardProps {
  provider: any
  account: string | null
  rewardsContract: any
  language: 'zh' | 'en'
}

interface UserDeposit {
  commitment: string
  amount: number
  depositTime: number
  multiplier: number
  withdrawn: boolean
  rewardsClaimed: boolean
}

interface ReferralStats {
  as_inviter: {
    total_invited: number
    activated_count: number
    total_bonus_points: number
  }
  as_invitee: {
    has_inviter: boolean
    inviter_address: string | null
    bonus_points: number
  }
}

export function MyDashboard({
  provider,
  account,
  rewardsContract,
  language
}: MyDashboardProps) {
  const [totalDeposited, setTotalDeposited] = useState(0)
  const [currentPoints, setCurrentPoints] = useState(0)
  const [displayPoints, setDisplayPoints] = useState(0)
  const [deposits, setDeposits] = useState<UserDeposit[]>([])
  const [loading, setLoading] = useState(false)
  const animationRef = useRef<number>()

  // Referral system state
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null)
  const [inviterInput, setInviterInput] = useState('')
  const [bindingInviter, setBindingInviter] = useState(false)
  const [bindMessage, setBindMessage] = useState('')
  const [showReferralLink, setShowReferralLink] = useState(false)

  const t = {
    zh: {
      title: '我的挖矿数据',
      totalDeposited: '累计存款',
      currentPoints: '当前积分',
      myDeposits: '我的存款记录',
      noDeposits: '暂无存款记录',
      depositAmount: '存款金额',
      depositTime: '存款时间',
      duration: '持续时间',
      multiplier: '倍率',
      status: '状态',
      active: '进行中',
      withdrawn: '已提款',
      days: '天',
      loading: '加载中...',
      pointsIncreasing: '积分持续增长中...',
      connectFirst: '请先连接钱包查看您的挖矿数据',
      // Referral
      referralTitle: '推荐邀请',
      myReferralCode: '我的邀请链接',
      copyLink: '复制链接',
      copied: '已复制！',
      bindInviter: '绑定邀请人',
      inviterAddress: '邀请人地址',
      bind: '绑定',
      binding: '绑定中...',
      alreadyBound: '已绑定邀请人',
      invitedUsers: '邀请人数',
      activatedUsers: '激活用户',
      referralBonus: '推荐奖励',
      myInviter: '我的邀请人',
      myReferralBonus: '我的推荐奖励',
      referralDescription: '推荐好友使用，双方各获得 20% 积分加成！'
    },
    en: {
      title: 'My Mining Stats',
      totalDeposited: 'Total Deposited',
      currentPoints: 'Current Points',
      myDeposits: 'My Deposits',
      noDeposits: 'No deposits yet',
      depositAmount: 'Amount',
      depositTime: 'Deposit Time',
      duration: 'Duration',
      multiplier: 'Multiplier',
      status: 'Status',
      active: 'Active',
      withdrawn: 'Withdrawn',
      days: 'days',
      loading: 'Loading...',
      pointsIncreasing: 'Points increasing...',
      connectFirst: 'Please connect your wallet to view your mining data',
      // Referral
      referralTitle: 'Referral Program',
      myReferralCode: 'My Referral Link',
      copyLink: 'Copy Link',
      copied: 'Copied!',
      bindInviter: 'Bind Inviter',
      inviterAddress: 'Inviter Address',
      bind: 'Bind',
      binding: 'Binding...',
      alreadyBound: 'Inviter Bound',
      invitedUsers: 'Invited Users',
      activatedUsers: 'Activated',
      referralBonus: 'Referral Bonus',
      myInviter: 'My Inviter',
      myReferralBonus: 'My Referral Bonus',
      referralDescription: 'Refer friends and both get 20% points bonus!'
    }
  }

  const content = t[language]

  // 模拟实时积分增长（参照 blur.io）
  useEffect(() => {
    if (currentPoints === 0) {
      setDisplayPoints(0)
      return
    }

    const diff = currentPoints - displayPoints
    if (Math.abs(diff) < 0.01) {
      setDisplayPoints(currentPoints)
      return
    }

    // 平滑动画
    const duration = 1000 // 1秒内完成动画
    const steps = 60 // 60 帧
    const increment = diff / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayPoints(prev => prev + increment)

      if (step < steps) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setDisplayPoints(currentPoints)
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [currentPoints, displayPoints])

  // 禁用本地积分计算，直接使用后端返回的积分
  // useEffect(() => {
  //   if (!deposits.length) return

  //   const interval = setInterval(() => {
  //     updatePoints()
  //   }, 1000)

  //   return () => clearInterval(interval)
  // }, [deposits])

  // 加载用户存款数据
  useEffect(() => {
    if (account && rewardsContract) {
      loadUserDeposits()
      loadReferralStats()
    }
  }, [account, rewardsContract])

  const loadUserDeposits = async () => {
    try {
      setLoading(true)

      if (!account) {
        setDeposits([])
        return
      }

      // 调用后端 API 获取存款历史
      try {
        // 获取存款历史
        const depositsRes = await fetch(`${API_URL}/api/deposits/${account}`)

        if (depositsRes.ok) {
          const deposits = await depositsRes.json()
          console.log('📊 从后端获取的存款:', deposits)

          // 转换数据格式
          const userDeposits: UserDeposit[] = deposits.map((d: any) => ({
            commitment: d.commitment,
            amount: Number(d.amount),
            depositTime: new Date(d.deposit_time).getTime(),
            multiplier: d.multiplier * 100, // 转换为百分比
            withdrawn: d.withdrawn,
            rewardsClaimed: d.withdrawn
          }))

          setDeposits(userDeposits)

          // 获取总积分
          const pointsRes = await fetch(`${API_URL}/api/points/${account}`)
          if (pointsRes.ok) {
            const pointsData = await pointsRes.json()
            console.log('✅ 积分数据:', pointsData)
            setCurrentPoints(pointsData.total_points)
            setTotalDeposited(Number(pointsData.total_amount) / 1e6)
          }

        } else if (depositsRes.status === 404) {
          // 用户未找到，可能还没有提款
          console.log('⚠️ 未找到存款记录（可能还未提款）')
          setDeposits([])
          setCurrentPoints(0)
          setTotalDeposited(0)
        } else {
          throw new Error('Failed to load deposits')
        }

      } catch (apiError) {
        console.error('❌ API 调用失败，尝试使用本地数据:', apiError)

        // 降级：使用本地数据
        const savedCommitments = JSON.parse(localStorage.getItem(`commitments_${account}`) || '[]')
        if (savedCommitments.length > 0) {
          console.log('💾 使用 localStorage 数据')
          // 这里可以添加本地数据的处理逻辑
        }
      }

    } catch (error) {
      console.error('❌ 加载用户存款失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 加载推荐统计数据
  const loadReferralStats = async () => {
    if (!account) return

    try {
      const response = await fetch(`${API_URL}/api/referral-stats/${account}`)
      if (response.ok) {
        const data = await response.json()
        setReferralStats(data)
        console.log('📊 推荐统计:', data)
      }
    } catch (error) {
      console.error('❌ 加载推荐统计失败:', error)
    }
  }

  // 绑定邀请人
  const handleBindInviter = async () => {
    if (!account || !inviterInput) return

    // 验证地址格式
    if (!ethers.isAddress(inviterInput)) {
      setBindMessage('❌ 无效的地址格式')
      setTimeout(() => setBindMessage(''), 3000)
      return
    }

    // 不能绑定自己
    if (inviterInput.toLowerCase() === account.toLowerCase()) {
      setBindMessage('❌ 不能绑定自己为邀请人')
      setTimeout(() => setBindMessage(''), 3000)
      return
    }

    setBindingInviter(true)
    setBindMessage('')

    try {
      const response = await fetch(`${API_URL}/api/bind-inviter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_address: account,
          inviter_address: inviterInput
        })
      })

      const data = await response.json()

      if (response.ok) {
        setBindMessage('✅ ' + data.message)
        setInviterInput('')
        // 重新加载推荐统计
        await loadReferralStats()
      } else {
        setBindMessage('❌ ' + data.detail)
      }
    } catch (error) {
      console.error('❌ 绑定邀请人失败:', error)
      setBindMessage('❌ 绑定失败，请重试')
    } finally {
      setBindingInviter(false)
      setTimeout(() => setBindMessage(''), 5000)
    }
  }

  // 复制推荐链接
  const copyReferralLink = () => {
    if (!account) return
    const link = `${window.location.origin}?ref=${account}`
    navigator.clipboard.writeText(link)
    setShowReferralLink(true)
    setTimeout(() => setShowReferralLink(false), 2000)
  }

  // 已弃用：本地积分计算，现在使用后端 API 返回的积分
  // const updatePoints = () => {
  //   if (!deposits.length) {
  //     setCurrentPoints(0)
  //     return
  //   }
  //   ...
  // }

  if (!account) {
    return (
      <div className="my-dashboard">
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <h2 style={{ fontSize: '1.8em', marginBottom: '20px' }}>📊 {content.title}</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '20px', fontSize: '1.1rem' }}>
            {content.connectFirst}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="my-dashboard">
      <h2 className="dashboard-title">{content.title}</h2>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="dashboard-stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-label">{content.totalDeposited}</div>
            <div className="stat-value">{totalDeposited.toLocaleString()}</div>
            <div className="stat-unit">USDT</div>
          </div>
        </div>

        <div className="dashboard-stat-card highlight">
          <div className="stat-icon">✨</div>
          <div className="stat-info">
            <div className="stat-label">{content.currentPoints}</div>
            <div className="stat-value animated">
              {Math.floor(displayPoints).toLocaleString()}
            </div>
            <div className="stat-hint">{content.pointsIncreasing}</div>
          </div>
        </div>
      </div>

      {/* Referral Section */}
      <div className="referral-section">
        <h3>🎁 {content.referralTitle}</h3>
        <p className="referral-description">{content.referralDescription}</p>

        <div className="referral-cards">
          {/* My Referral Link */}
          <div className="referral-card">
            <h4>{content.myReferralCode}</h4>
            <div className="referral-link-box">
              <input
                type="text"
                value={account ? `${window.location.origin}?ref=${account}` : ''}
                readOnly
                className="referral-link-input"
              />
              <button onClick={copyReferralLink} className="copy-button">
                {showReferralLink ? content.copied : content.copyLink}
              </button>
            </div>
          </div>

          {/* Bind Inviter or Show Inviter Info */}
          <div className="referral-card">
            {referralStats?.as_invitee.has_inviter ? (
              <>
                <h4>{content.alreadyBound}</h4>
                <div className="inviter-info">
                  <div className="info-row">
                    <span className="info-label">{content.myInviter}:</span>
                    <span className="info-value address">
                      {referralStats.as_invitee.inviter_address?.substring(0, 6)}...
                      {referralStats.as_invitee.inviter_address?.substring(38)}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{content.myReferralBonus}:</span>
                    <span className="info-value highlight">
                      +{referralStats.as_invitee.bonus_points.toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h4>{content.bindInviter}</h4>
                <div className="bind-inviter-box">
                  <input
                    type="text"
                    value={inviterInput}
                    onChange={(e) => setInviterInput(e.target.value)}
                    placeholder={content.inviterAddress}
                    className="inviter-input"
                    disabled={bindingInviter}
                  />
                  <button
                    onClick={handleBindInviter}
                    disabled={bindingInviter || !inviterInput}
                    className="bind-button"
                  >
                    {bindingInviter ? content.binding : content.bind}
                  </button>
                </div>
                {bindMessage && (
                  <div className={`bind-message ${bindMessage.startsWith('✅') ? 'success' : 'error'}`}>
                    {bindMessage}
                  </div>
                )}
              </>
            )}
          </div>

          {/* As Inviter Stats */}
          <div className="referral-card">
            <h4>📈 {content.referralBonus}</h4>
            <div className="referral-stats-grid">
              <div className="stat-item">
                <div className="stat-value">{referralStats?.as_inviter.total_invited || 0}</div>
                <div className="stat-label">{content.invitedUsers}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{referralStats?.as_inviter.activated_count || 0}</div>
                <div className="stat-label">{content.activatedUsers}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value highlight">
                  +{referralStats?.as_inviter.total_bonus_points.toLocaleString() || 0}
                </div>
                <div className="stat-label">{content.referralBonus}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deposits List */}
      <div className="deposits-section">
        <h3>{content.myDeposits}</h3>

        {loading ? (
          <div className="loading-state">{content.loading}</div>
        ) : deposits.length === 0 ? (
          <div className="no-deposits">
            <div className="no-deposits-icon">📭</div>
            <p>{content.noDeposits}</p>
          </div>
        ) : (
          <div className="deposits-list">
            {deposits.map((deposit, index) => {
              const duration = Math.floor((Date.now() - deposit.depositTime) / 1000 / 86400)
              return (
                <div key={index} className={`deposit-item ${deposit.withdrawn ? 'withdrawn' : ''}`}>
                  <div className="deposit-info">
                    <div className="deposit-amount">
                      {(deposit.amount / 1e6).toLocaleString()} USDT
                    </div>
                    <div className="deposit-meta">
                      <span>{new Date(deposit.depositTime).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{duration} {content.days}</span>
                      <span>•</span>
                      <span>{deposit.multiplier / 100}x</span>
                    </div>
                  </div>
                  <div className="deposit-status">
                    {deposit.withdrawn ? content.withdrawn : content.active}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
